/**
 * Centralized error handling utilities
 * Provides consistent error logging and user feedback
 */

/**
 * Logs error with context information
 * @param {Error} error - Error object
 * @param {string} context - Context where error occurred
 * @param {Object} additionalInfo - Additional information to log
 */
export function logError(error, context = '', additionalInfo = {}) {
	const errorInfo = {
		message: error?.message || 'Unknown error',
		stack: error?.stack,
		context,
		timestamp: new Date().toISOString(),
		...additionalInfo,
	};

	console.error(`[${context}] Error:`, errorInfo);

	// In production, you might want to send this to an error tracking service
	// like Sentry, LogRocket, or custom logging service
	if (typeof window !== 'undefined' && process.env.NODE_ENV === 'production') {
		// Example: Send to error tracking service
		// trackError(errorInfo);
	}
}

/**
 * Handles API errors consistently
 * @param {Error} error - Error object
 * @param {string} context - API endpoint or action context
 * @returns {Object} Standardized error response
 */
export function handleApiError(error, context = 'API Request') {
	logError(error, context);

	// Return user-friendly error message
	if (error?.response) {
		// Server responded with error status
		return {
			success: false,
			message: 'Server error occurred. Please try again later.',
			status: error.response.status,
		};
	} else if (error?.request) {
		// Request made but no response
		return {
			success: false,
			message: 'Network error. Please check your connection.',
		};
	} else {
		// Error in request setup
		return {
			success: false,
			message: error?.message || 'An unexpected error occurred',
		};
	}
}

/**
 * Wraps async functions with error handling
 * @param {Function} fn - Async function to wrap
 * @param {string} context - Context for error logging
 * @returns {Function} Wrapped function with error handling
 */
export function withErrorHandling(fn, context = 'Operation') {
	return async (...args) => {
		try {
			return await fn(...args);
		} catch (error) {
			logError(error, context);
			throw error; // Re-throw to let caller handle
		}
	};
}

/**
 * Creates a user-friendly error message from technical errors
 * @param {Error} error - Error object
 * @returns {string} User-friendly message
 */
export function getUserFriendlyMessage(error) {
	const message = error?.message?.toLowerCase() || '';

	if (message.includes('network') || message.includes('fetch')) {
		return 'Network error. Please check your internet connection.';
	}
	if (message.includes('timeout')) {
		return 'Request timed out. Please try again.';
	}
	if (message.includes('unauthorized') || message.includes('401')) {
		return 'You are not authorized to perform this action.';
	}
	if (message.includes('not found') || message.includes('404')) {
		return 'The requested resource was not found.';
	}
	if (message.includes('validation') || message.includes('400')) {
		return 'Please check your input and try again.';
	}

	return 'An unexpected error occurred. Please try again later.';
}
